/* Apolos account details -> standard Shopify checkout.
 * Save this file in Shopify Assets as apolos-checkout.js.
 * Load after apolos-session.js in theme.liquid, before </body>:
 * <script src="{{ 'apolos-checkout.js' | asset_url }}" defer></script>
 * Uses the existing cart and the Worker's verified account response.
 * Custom JS checkout buttons can call window.ApolosCheckout.go().
 * Coverage: ordinary same-origin Checkout links and native cart forms.
 * Express wallets and direct visits to an existing checkout bypass theme JS.
 * Prefilling fields does not establish a Shopify customer login session.
 */
(function () {
  'use strict';
  if (window.ApolosCheckout) return;

  var CACHE_MS = 30000;
  var WAIT_MS = 4000;
  var cache = null;
  var cacheUntil = 0;
  var pending = null;
  var generation = 0;
  var navigating = false;
  var waitingForms = new WeakSet();
  var resumingForms = new WeakSet();
  var restorers = new Set();

  function storefrontRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function allowedURL(input, allowCartForm) {
    var url;
    try { url = new URL(input, window.location.href); }
    catch (error) { return null; }
    if (url.origin !== window.location.origin || url.username || url.password) return null;

    var prefix = new URL(storefrontRoot(), window.location.origin).pathname;
    if (!prefix.endsWith('/')) prefix += '/';
    var paths = ['/checkout', '/cart/checkout', prefix + 'checkout', prefix + 'cart/checkout'];
    if (allowCartForm) paths.push('/cart', prefix + 'cart');
    var path = url.pathname.replace(/\/$/, '');
    return paths.indexOf(path) !== -1 ? url : null;
  }

  function defaultCheckoutURL() {
    return new URL(storefrontRoot() + 'cart/checkout', window.location.origin).href;
  }

  function fieldsFrom(data) {
    if (!data || data.authenticated !== true || !data.customer ||
        !Number.isSafeInteger(data.expires_at) || data.expires_at <= Date.now()) return null;

    var customer = data.customer;
    var address = data.profile || {};
    var fields = {};
    function add(key, value) {
      if (typeof value === 'string' && value.trim()) fields[key] = value.trim();
    }
    add('checkout[email]', customer.email);
    add('checkout[shipping_address][first_name]', customer.first_name);
    add('checkout[shipping_address][last_name]', customer.last_name);
    add('checkout[shipping_address][company]', address.company);
    add('checkout[shipping_address][address1]', address.address1);
    add('checkout[shipping_address][address2]', address.address2);
    add('checkout[shipping_address][city]', address.city);
    add('checkout[shipping_address][province]', address.province);
    add('checkout[shipping_address][country]', address.country_code || address.country);
    add('checkout[shipping_address][zip]', address.zip);
    add('checkout[shipping_address][phone]', address.phone);
    return { fields: fields, expiresAt: data.expires_at };
  }

  function invalidate() {
    generation++;
    cache = null;
    cacheUntil = 0;
    pending = null;
  }

  function loadDetails() {
    if (Date.now() < cacheUntil && (!cache || Date.now() < cache.expiresAt)) {
      return Promise.resolve(cache);
    }
    if (pending) return pending;
    if (!window.ApolosSession || typeof window.ApolosSession.accountHub !== 'function') {
      return Promise.resolve(null);
    }

    var version = generation;
    var request = Promise.resolve().then(function () {
      return window.ApolosSession.accountHub('read');
    }).then(function (data) {
      if (version !== generation) return null;
      cache = fieldsFrom(data);
      cacheUntil = Date.now() + CACHE_MS;
      return cache;
    }).catch(function () {
      // An unavailable account service must not prevent guest checkout.
      return null;
    }).finally(function () {
      if (pending === request) pending = null;
    });
    pending = request;
    return request;
  }

  async function detailsForCheckout() {
    var version = generation;
    var timer;
    try {
      var details = await Promise.race([
        loadDetails(),
        new Promise(function (resolve) {
          timer = setTimeout(function () { resolve(null); }, WAIT_MS);
        })
      ]);
      if (version !== generation || !details || Date.now() >= details.expiresAt) return null;
      return { fields: details.fields, expiresAt: details.expiresAt, generation: version };
    } finally {
      clearTimeout(timer);
    }
  }

  function addDetails(url, details) {
    if (details && details.generation === generation && Date.now() < details.expiresAt) {
      Object.keys(details.fields).forEach(function (key) {
        // Preserve explicitly supplied checkout values and all other parameters.
        if (!url.searchParams.has(key)) url.searchParams.set(key, details.fields[key]);
      });
    }
    return url.href;
  }

  async function getCheckoutURL(input) {
    var url = allowedURL(input || defaultCheckoutURL(), false);
    if (!url) throw new Error('Unsupported checkout destination.');
    return addDetails(url, await detailsForCheckout());
  }

  async function go(input) {
    if (navigating) return;
    navigating = true;
    var version = generation;
    try {
      var destination = await getCheckoutURL(input);
      if (version !== generation) {
        destination = allowedURL(input || defaultCheckoutURL(), false).href;
      }
      window.location.assign(destination);
    } finally {
      navigating = false;
    }
  }

  // Delegate so AJAX-rendered cart drawers work without rebinding listeners.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey) return;
    var target = event.target;
    var link = target && target.closest ? target.closest('a[href]') : null;
    if (!link || link.hasAttribute('download') ||
        (link.target && link.target.toLowerCase() !== '_self') ||
        link.closest('[data-apolos-checkout-skip], .shopify-payment-button, shopify-accelerated-checkout')) return;
    var url = allowedURL(link.href, false);
    if (!url) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    go(url.href).catch(function () {
      window.location.assign(url.href);
    });
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented ||
        resumingForms.has(form) || typeof form.requestSubmit !== 'function') return;
    if (form.closest('[data-apolos-checkout-skip]')) return;
    var button = event.submitter;
    if (button && button.closest('[data-apolos-checkout-skip], .shopify-payment-button, shopify-accelerated-checkout')) return;

    var actionOwner = button && button.hasAttribute('formaction') ? button : form;
    var attribute = actionOwner === form ? 'action' : 'formaction';
    var action = actionOwner === form ? form.action : button.formAction;
    var url = allowedURL(action, true);
    if (!url) return;
    var isCheckout = !!(button && button.name === 'checkout') || !!allowedURL(action, false);
    if (!isCheckout) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (waitingForms.has(form)) return;
    waitingForms.add(form);
    var busyValue = form.getAttribute('aria-busy');
    form.setAttribute('aria-busy', 'true');

    detailsForCheckout().then(function (details) {
      if (!form.isConnected || (button && (!button.isConnected || button.disabled))) return;
      var originalAction = actionOwner.getAttribute(attribute);
      var decoratedAction = addDetails(url, details);
      function restore() {
        restorers.delete(restore);
        if (actionOwner.getAttribute(attribute) !== decoratedAction) return;
        if (originalAction === null) actionOwner.removeAttribute(attribute);
        else actionOwner.setAttribute(attribute, originalAction);
      }
      actionOwner.setAttribute(attribute, decoratedAction);
      restorers.add(restore);
      resumingForms.add(form);
      try {
        // Keep the original POST, quantities, notes, attributes, submitter,
        // validation and theme submit handlers. Never recreate the cart.
        if (button) form.requestSubmit(button);
        else form.requestSubmit();
      } finally {
        resumingForms.delete(form);
        setTimeout(restore, 0);
      }
    }).catch(function () {
      // Resume the untouched form if preparing the handoff fails.
      if (!form.isConnected || (button && (!button.isConnected || button.disabled))) return;
      resumingForms.add(form);
      try {
        if (button) form.requestSubmit(button);
        else form.requestSubmit();
      } finally {
        resumingForms.delete(form);
      }
    }).finally(function () {
      waitingForms.delete(form);
      if (busyValue === null) form.removeAttribute('aria-busy');
      else form.setAttribute('aria-busy', busyValue);
    });
  }, true);

  window.addEventListener('apolos:session-changed', invalidate);
  window.addEventListener('pagehide', function () {
    invalidate();
    restorers.forEach(function (restore) { restore(); });
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      invalidate();
      navigating = false;
      loadDetails();
    }
  });

  window.ApolosCheckout = Object.freeze({
    go: go,
    getCheckoutURL: getCheckoutURL
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadDetails, { once: true });
  } else {
    loadDetails();
  }
})();